import {
  formatShortDateAndTimeOrSameDay,
  formatShortDateOrSameDay,
  IntegrationProvider,
  IntegrationRunStatus,
} from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { ActivityCard } from '../crop-circle/Components/ActivityCard';
import { INTEGRATION_PROVIDER_NAMES } from './integrationAttributionTag';

/**
 * Shape of one row returned by the `integration.syncDigests` resolver.
 *
 * The fields here are the subset needed for the digest card renderer; the
 * resolver returns additional per-asset breakdown data used by the
 * right-panel digest detail.
 */
export interface IntegrationSyncDigestData {
  /** Unique key for the digest row (e.g. "GEOTAB:2024-06-11"). */
  digestKey: string;
  /** Integration provider that produced this digest. */
  provider: IntegrationProvider;
  /** ISO date string for the day this digest covers (YYYY-MM-DD). */
  day: string;
  /** Number of distinct assets that had at least one reading written. */
  assetsSynced: number;
  /** Total number of routine readings written across all assets. */
  readingsWritten: number;
  /** Number of new fault issues opened during this sync window. */
  issuesOpened: number;
  /** ISO timestamp of the most recent sync run that contributed to this digest. */
  lastSyncAt: string;
  /**
   * Status of the last sync run.  When FAILED the card switches to the yellow
   * blocker variant and shows a warning message.
   */
  lastRunStatus: IntegrationRunStatus | null;
}

/**
 * Per-asset breakdown row inside a digest, rendered by the right-panel digest
 * detail (`IntegrationDigestPanel`). Tapping a row navigates to the asset
 * detail page. Readings are silent — there is no per-reading navigation.
 */
export interface IntegrationDigestAsset {
  assetId: string;
  assetName: string;
  readingsWritten: number;
  issueCount: number;
}

/**
 * The full `integration.syncDigests` row as returned by the resolver — the
 * card-facing subset (`IntegrationSyncDigestData`) plus the fields the feed
 * section needs: `issuesUpdated` and the per-asset `assets` breakdown.
 *
 * This is the frozen Phase-4 frontend contract (gripp-fsd.2 notes).
 */
export interface IntegrationDigestRow extends IntegrationSyncDigestData {
  /** Count of existing issues escalated/updated in this window (0 until Phase 3). */
  issuesUpdated: number;
  /** Per-asset breakdown for the right-panel digest detail. */
  assets: IntegrationDigestAsset[];
}

export interface IntegrationSyncDigestCardProps {
  digest: IntegrationSyncDigestData;
  /**
   * Name shown in the gray pill slot — the workspace name, matching what the
   * sibling rows put there (`asset.name || workspace.name`). A digest is a
   * workspace-wide rollup with no single asset, so it falls back to the
   * workspace name. Omitted ⇒ no pill (don't render an empty chip).
   */
  workspaceName?: string;
  isUnread?: boolean;
  isSelected?: boolean;
  borderRadius?: number;
}

/**
 * Feed row for an aggregated daily integration sync digest.  Renders as a
 * standard activity-list row (`ActivityCard`), indistinguishable in idiom from
 * the sibling issue/campaign rows: QR-code placeholder thumbnail (like a
 * photo-less issue row), provider display name in the attribution pill slot,
 * standard timestamp placement.
 *
 * - Blue `'routines'` variant normally — integration readings are silent, this
 *   is the one surface they appear on.
 * - Yellow `'blocker'` variant when `lastRunStatus === FAILED` with a
 *   "Sync failed — readings may be out of date" warning description.
 * - The gray pill slot shows the WORKSPACE name (the `workspaceName` prop),
 *   matching what sibling rows put there (`asset.name || workspace.name`). The
 *   provider is named in the title, not the pill, so the row reads as ordinary.
 * - Totals are PLAIN TEXT in the row title (no count pills): e.g. "GeoTab
 *   synced 42 assets · 78 readings · 31 faults". The fault segment is omitted
 *   when `issuesOpened === 0`.
 * - Default QR placeholder thumbnail (`showImage` left at its default) — digest
 *   rows are system-generated and have no asset image, so they fall through to
 *   the same QR placeholder photo-less issue rows show.
 *
 * The per-asset breakdown lives in a right-hand SlideLayout detail panel opened
 * on tap (see `IntegrationDigestSection` / the `activities/digest/:digestKey`
 * route), not an expand-in-place list.
 */
export const IntegrationSyncDigestCard = (
  props: IntegrationSyncDigestCardProps
) => {
  const { t } = useTranslation();
  const { digest } = props;

  const isWeb = Platform.OS === 'web';
  const formattedDate = digest.lastSyncAt
    ? isWeb
      ? formatShortDateAndTimeOrSameDay(digest.lastSyncAt)
      : formatShortDateOrSameDay(digest.lastSyncAt)
    : '';

  const providerName =
    INTEGRATION_PROVIDER_NAMES[digest.provider] ?? digest.provider;

  // Compare via the enum's string value to keep the comparison correct even
  // in test environments where the mocked module spread may not initialise
  // enum objects before component render.
  const isFailed = (digest.lastRunStatus as string) === 'FAILED';

  // Title is plain text with the counts inlined (no pills). Each segment uses
  // its own plural key so grammar stays correct per number; the fault segment
  // is dropped when no faults were opened.
  const titleSegments = [
    t('integrations.digest.titleSegment', {
      provider: providerName,
      count: digest.assetsSynced,
    }),
    t('integrations.digest.readingsCount', {
      count: digest.readingsWritten,
    }),
    ...(digest.issuesOpened > 0
      ? [
          t('integrations.digest.faultsCount', {
            count: digest.issuesOpened,
          }),
        ]
      : []),
  ];
  const title = titleSegments.join(' · ');

  const description = isFailed
    ? t('integrations.digest.failedDescription')
    : undefined;

  return (
    <ActivityCard
      // Workspace name in the same pill slot sibling rows fill with
      // `asset.name || workspace.name`. A digest spans the whole workspace, so
      // the workspace name is the right fallback — it reads as an ordinary row,
      // not a provider-branded one. (The provider is still named in the title.)
      assetName={props.workspaceName ?? ''}
      showAssetName={!!props.workspaceName}
      // showImage left at its default (true): with no imageUrl, ActivityCard
      // renders the QR placeholder — the same thumbnail treatment photo-less
      // issue rows get, so the digest reads as an ordinary feed row.
      title={title}
      date={formattedDate}
      description={description}
      severityVariant={isFailed ? 'blocker' : 'routines'}
      isUnread={props.isUnread}
      isSelected={props.isSelected}
      borderRadius={props.borderRadius}
    />
  );
};

IntegrationSyncDigestCard.displayName = 'IntegrationSyncDigestCard';
